import { useState } from 'react'
import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useQuery } from '@/api/query'
import { formatDate } from '@/lib/format'
import { SpendPanel } from '@/components/SpendPanel'
import { Empty, ErrorState, Loading } from '@/ui/states'
import { AccountHealthPanel } from './AccountHealthPanel'
import { DeliverabilityPanel } from './DeliverabilityPanel'
import { SendWindowPanel } from './SendWindowPanel'

type Funnel = Schema<'FunnelResponse'>
type Stage = Schema<'FunnelStageOut'>
type Channel = Schema<'FunnelChannelOut'>

/** The windows `app/outreach/funnel.WINDOWS` offers, and the only ones it accepts. */
const WINDOWS = [7, 30, 90] as const
type Window = (typeof WINDOWS)[number]

/**
 * The outreach dashboard's headline: S-D4's funnel, on real data (S-B31a).
 *
 * **Why the funnel is the whole top of the screen** is the design's own note,
 * and it is worth keeping: *"outreach only has one question — is anything coming
 * out the other end"*. Deliverability, ban risk and spend sit beside it in
 * S-B31b and S-B31c; this file owns the funnel and the per-channel split.
 *
 * **Every number comes from the endpoint, including the captions and the bar
 * widths.** Nothing is recomputed here — not a percentage, not a share. The
 * server decides what a stage is a share of (`replied` is quoted against *sent*
 * rather than against the stage drawn before it, and the module says why), so a
 * change to that rule lands on the screen without a second implementation of it
 * drifting in TypeScript.
 *
 * **A caption that is absent is absent, not zero.** `note` is `null` when the
 * stage it would be a share of is empty, and this renders nothing rather than
 * `0.0%` — a tenant who has sent nothing does not have a 0% open rate.
 *
 * **The empty state asks the right question.** `empty` and `ever_active` are two
 * different facts: a quiet month wants a wider window, a brand-new organisation
 * wants a connected channel, and offering the first to somebody in the second
 * state is advice that cannot work.
 */
export function DashboardPage(): JSX.Element {
  const [days, setDays] = useState<Window>(30)

  const funnel = useQuery(
    (signal) =>
      call(() => api.GET('/api/v1/outreach/funnel', { params: { query: { days } }, signal })),
    [days],
  )

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row" style={{ justifyContent: 'space-between' }}>
        <h1 className="ar-h1">Outreach</h1>
        <div className="ar-btn-group" role="group" aria-label="Period">
          {WINDOWS.map((window) => (
            <button
              key={window}
              type="button"
              className={`ar-btn ar-btn--sm ${days === window ? 'ar-btn--primary' : 'ar-btn--ghost'}`}
              // `aria-pressed` rather than `aria-current`: these are toggle
              // buttons over one value, not links to three places.
              aria-pressed={days === window}
              onClick={() => {
                setDays(window)
              }}
            >
              Last {window} days
            </button>
          ))}
        </div>
      </div>

      {funnel.state.status === 'loading' ? <Loading label="Loading your funnel" lines={4} /> : null}

      {funnel.state.status === 'error' ? (
        <div className="ar-card">
          <ErrorState
            failure={funnel.state.failure}
            title="Could not load your funnel"
            onRetry={funnel.refetch}
          />
        </div>
      ) : null}

      {funnel.state.status === 'ready' ? <Ready data={funnel.state.data} /> : null}

      {/* **Five panels, five queries, five failure modes.** The funnel is an
          aggregate over this tenant's own tables, account health joins those to
          `public.jobs`, the send window reads the `automation` singleton,
          deliverability asks somebody else's nameservers, and spend reads the
          ledger in `public` — so one of them being unavailable must not blank
          the other four. Each owns its loading, empty and error states
          (S-B31b, S-B31c, C1).

          **Spend is last on purpose.** S-D4's own note says outreach has one
          question — *is anything coming out the other end* — so the funnel is
          the top of the screen and what it cost is the footnote to it, not the
          headline. */}
      <AccountHealthPanel />
      {/* **The send window sits directly under account health (C1)**, which is
          where `design/screens/outreach.html#health` puts it and why: it is the
          one control on this screen that changes the ban risk the panel above
          has just reported, so the reading and the lever are adjacent. Its own
          query and its own error state, like the four around it. */}
      <SendWindowPanel />
      <DeliverabilityPanel />
      <SpendPanel module="outreach" />
    </div>
  )
}

function Ready({ data }: { data: Funnel }): JSX.Element {
  if (data.empty) return <Nothing data={data} />
  return (
    <>
      <div className="app-funnel">
        {data.stages.map((stage) => (
          <StageCard key={stage.key} stage={stage} />
        ))}
      </div>
      <ByChannel data={data} />
    </>
  )
}

/**
 * The empty state, in its two forms.
 *
 * A real state rather than six zeroes, which is S-D1's rule for one — *never a
 * bare "no data": it says what would be here and how to get it* — and this
 * session's exit criterion.
 */
function Nothing({ data }: { data: Funnel }): JSX.Element {
  return (
    <div className="ar-card">
      {data.ever_active ? (
        <Empty
          icon="✉"
          title={`Nothing in the last ${String(data.days)} days`}
          body="There is outreach history on this organisation, just not in this period. Try a wider window, or start a discovery run to find new prospects."
          action={
            <Link className="ar-btn ar-btn--ghost" to="/settings/connections">
              Open connections
            </Link>
          }
        />
      ) : (
        <Empty
          icon="✉"
          title="Nothing has gone out yet"
          body="Connect LinkedIn and a sender address, and lead discovery fills this funnel: posts found become leads, leads become sends, and replies land in your inbox."
          action={
            <Link className="ar-btn ar-btn--primary" to="/settings/connections">
              Connect a channel
            </Link>
          }
        />
      )}
    </div>
  )
}

/** One of the six stages: its number, its bar, and the line under it. */
function StageCard({ stage }: { stage: Stage }): JSX.Element {
  return (
    <div className="app-stage">
      <div className="app-stage__label">{stage.label}</div>
      <div className="app-stage__n">{stage.value.toLocaleString()}</div>
      <div
        className="app-bar"
        role="meter"
        aria-label={`${stage.label}, ${String(stage.share_of_first)}% of the first stage`}
        aria-valuenow={stage.share_of_first}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <i className="app-bar__fill" style={{ width: `${String(stage.share_of_first)}%` }} />
      </div>
      {/* Rendered as an empty element rather than omitted, so the six cards keep
          one height when a stage has no rate to state. */}
      <div className="app-stage__note">{stage.note ?? ''}</div>
    </div>
  )
}

/**
 * The per-channel table.
 *
 * **Every channel in the catalogue has a row, including the ones nobody has
 * connected**, because an absent row and a zero row are the same pixel and only
 * one of them has something to do about it.
 */
function ByChannel({ data }: { data: Funnel }): JSX.Element {
  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h3>By channel</h3>
        <span className="ar-card__spacer" />
        <span className="ar-subtle">since {formatDate(data.since)}</span>
      </div>
      <div className="ar-table-wrap">
        <table className="ar-table">
          <thead>
            <tr>
              <th>Channel</th>
              <th className="ar-num">Sent</th>
              <th className="ar-num">Opened</th>
              <th className="ar-num">Replied</th>
              <th className="ar-num">Reply rate</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.channels.map((channel) => (
              <ChannelRow key={channel.channel} channel={channel} />
            ))}
          </tbody>
        </table>
        <p className="ar-subtle app-funnel__caption">
          A person who answers on two channels counts once in the funnel above and once in each row
          here, so these replies can add up to more than the headline. Email shows no replies
          because inbound email is not something AugRank can receive; LinkedIn shows no sends
          because its API carries no messages at all.
        </p>
      </div>
    </section>
  )
}

function ChannelRow({ channel }: { channel: Channel }): JSX.Element {
  return (
    <tr>
      <td>
        <span className={`app-chan app-chan--${channel.channel}`}>{channel.label}</span>
      </td>
      <td className="ar-num">{channel.sent.toLocaleString()}</td>
      <td className="ar-num">{channel.opened.toLocaleString()}</td>
      {/* An em dash rather than 0 where a reply can never arrive: zero is a
          measurement, and this is not one. */}
      <td className="ar-num">{channel.receivable ? channel.replied.toLocaleString() : '—'}</td>
      <td className="ar-num">
        {channel.reply_rate === null ? '—' : `${channel.reply_rate.toFixed(1)}%`}
      </td>
      <td>
        <Status channel={channel} />
      </td>
    </tr>
  )
}

/**
 * Why this channel is or is not carrying anything.
 *
 * Three answers, and the order matters: a channel that can *never* send is not
 * "not connected", and telling somebody to connect LinkedIn for messaging would
 * send them looking for a setting that does not exist.
 */
function Status({ channel }: { channel: Channel }): JSX.Element {
  if (!channel.sendable) {
    return <span className="ar-badge ar-badge--neutral">No messaging API</span>
  }
  if (!channel.connected) {
    return (
      <span className="ar-badge ar-badge--neutral" title={channel.reason ?? undefined}>
        Not connected
      </span>
    )
  }
  if (channel.sent === 0) {
    return <span className="ar-badge ar-badge--neutral">Nothing sent</span>
  }
  return <span className="ar-badge ar-badge--success">Sending</span>
}
